/** Shared decoded-buffer cache: coalesced requests, bounded work and LRU retention. */
export interface AudioBufferCacheOptions<T> {
    load: (url: string) => Promise<T | null>;
    sizeOf: (value: T) => number;
    maxBytes: number;
    concurrency: number;
}

interface CacheEntry<T> {
    promise: Promise<T | null>;
    resolve: (value: T | null) => void;
    bytes: number;
    ready: boolean;
}

export function createAudioBufferCache<T>({
    load,
    sizeOf,
    maxBytes,
    concurrency,
}: AudioBufferCacheOptions<T>) {
    const entries = new Map<string, CacheEntry<T>>();
    const queue: string[] = [];
    let running = 0;
    let retainedBytes = 0;

    const evict = (): void => {
        for (const [url, entry] of entries) {
            if (retainedBytes <= maxBytes) break;
            if (!entry.ready) continue;
            retainedBytes -= entry.bytes;
            entries.delete(url);
        }
    };
    const pump = (): void => {
        while (running < Math.max(1, concurrency) && queue.length > 0) {
            const url = queue.shift();
            if (url === undefined) break;
            const entry = entries.get(url);
            if (entry === undefined) continue;
            running += 1;
            void Promise.resolve()
                .then(() => load(url))
                .catch(() => null)
                .then((value) => {
                    if (value === null) {
                        entries.delete(url);
                    } else {
                        entry.ready = true;
                        entry.bytes = sizeOf(value);
                        retainedBytes += entry.bytes;
                        evict();
                    }
                    entry.resolve(value);
                    running -= 1;
                    pump();
                });
        }
    };

    return {
        /** Playback requests move ahead of queued background warming. */
        get: (url: string, urgent = true): Promise<T | null> => {
            const existing = entries.get(url);
            if (existing !== undefined) {
                entries.delete(url);
                entries.set(url, existing);
                const queued = queue.indexOf(url);
                if (urgent && queued >= 0) {
                    queue.splice(queued, 1);
                    queue.unshift(url);
                }
                return existing.promise;
            }
            let resolve: (value: T | null) => void = () => undefined;
            const promise = new Promise<T | null>((done) => {
                resolve = done;
            });
            entries.set(url, { promise, resolve, ready: false, bytes: 0 });
            if (urgent) queue.unshift(url);
            else queue.push(url);
            pump();
            return promise;
        },
    };
}
